import { DeleteOutlined, EditOutlined, FilterFilled, SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Col, Menu, Row } from 'antd';
import { noop } from 'lodash';
import { httpCreators, Tags, useLoadingSelector } from '@ppvrbh/merchant-frontend-core';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { generalCreators } from '../../../redux/general/general-action';
import { DEFAULT_PAGE_SIZE, MODAL_TYPE } from '../../../redux/general/general-constant';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../redux/general/general-reducer';
import {
  RBHBox,
  RBHButton,
  RBHEmpty,
  RBHError,
  RBHImage,
  RBHTable,
  RBHText,
  RBHTextInput
  } from '../../../shared/components';
import RBHBanner from '../../../shared/components/rbh-banner';
import styleButton from '../../../shared/components/rbh-button/styles';
import styleImage from '../../../shared/components/rbh-image/styles';
import RBHQCTag from '../../../shared/components/rbh-qc-tag';
import styleText from '../../../shared/components/rbh-text/styles';
import styleInput from '../../../shared/components/rbh-textinput/styles';
import { UtilHelper } from '../../../shared/helpers/utility';
import { Colors, Fonts, Images } from '../../../shared/theme';
import metrics from '../../../shared/theme/metrics';
import { mainCreators } from '../../main-action';
import { MainHelper } from '../../main-helper';
import { MAIN_STATE_KEY } from '../../main-reducer';
import { catCreators } from '../FoodCategory/redux/cat-action';
import { FormAddEditMenu } from './components/FormAddEdit';
import { foodMenuCreators } from './redux/foodmenu-action';
import { FOODMENU_REQUEST_ID_LIST, MNQC_STATUS } from './redux/foodmenu-constant';
import { FOOD_MENU_KEY, foodMenuStateSelector } from './redux/foodmenu-reducer';
import { foodMenuSagas } from './redux/foodmenu-saga';
import { FoodMenuService } from './redux/foodmenu-service';
import styles from './styles';
import './styles.css';
import { reportCreators } from '../Report/redux/report-action';
import { FoodMenuType } from '../../../shared/models/food-menu';
import { MenuDiscountLabel } from './components/MenuDiscountLabel';
import { textStyles } from '../../../shared/theme/fonts-ppv';

export const FoodMenuScreen: React.FC<Props> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ editMenu?: FoodMenuType }>();
  const foodMenuData = useSelector(foodMenuStateSelector(FOOD_MENU_KEY.FOOD_MENU)) as FoodMenuType[];
  const loading = useLoadingSelector(FOODMENU_REQUEST_ID_LIST.GET_FOOD_MENU);
  const menuStatusCode = MainHelper.getMasterDataByScheme('MenuStatus');
  const [edit, setEdit] = useState<FoodMenuType | null>(location.state?.editMenu || null);
  const appConfig = useSelector(generalStateSelector(GENERAL_STATE_KEY.REMOTE_CONFIG))
  const enableStrikethrough = useMemo(() => {
    if (Array.isArray(appConfig)) {
      return appConfig.find(item => item.key === 'feature.promotion_strikethrough')?.value === 'true';
		}
    return false;
  }, [appConfig])

  const reload = () => {
    dispatch(foodMenuCreators.callFoodMenu());
    dispatch(reportCreators.callConfigAndShopList(() => {}, () => {}))
  };

  useEffect(() => {
    dispatch(mainCreators.setMainState(MAIN_STATE_KEY.HEADER_MENU, 5));
    reload();
  }, []);

  const resetReload = () => {
    if (location.state?.editMenu?.menuCategory?.menuCategoryId) {
      history.push('/main/foodcategory/' + location.state.editMenu.menuCategory.menuCategoryId)
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setEdit(null);
      reload();
    }
  };


  const pushToMain = () => {
    history.push('/main');
  };

  const pushToMenu = () => {
    resetReload();
  };

  const onMenuItemClick = (setSelectedKeys: any, selectedKeys: any, confirm: any, clearFilters: any, value: any) => {
    if (selectedKeys[0] === value) {
      clearFilters();
    } else {
      setSelectedKeys([value]);
      confirm();
    }
  };

  const deleteMenu = (foodMenuId: string) => {
    dispatch(httpCreators.request({
      id: FOODMENU_REQUEST_ID_LIST.DELETE_FOOD_MENU,
      sagaHandler: foodMenuSagas.deleteFoodMenuHandler as any,
      callbackParams: { resetReload },
      service: FoodMenuService.deleteFoodMenus,
      serviceParams: foodMenuId
    }));
  };

  const dispatchModal = (menuId: string) => {
    dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
      visible: true,
      onOk: () => deleteMenu(menuId),
      type: MODAL_TYPE.DELETE,
      modalTitle: 'main.model_delete_menu_title',
      modalContent: 'main.model_delete_menu_content'
    }));
  };

  const columns = [
    {
      width: metrics.tableHeader.width350,
      title: <FormattedMessage id={'main.foodmenu.landing.table.name'} />,
      className: 'menu-name-header',
      dataIndex: 'menuName',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => {
        return (
          <RBHBox style={{ padding: '14px 10px' }}>
            <RBHTextInput
              placeholder={'main.foodmenu.landing.table.name'}
              data-testid={'search-menu-input'}
              id={'search-menu-input'}
              style={styleInput.inputTableSearch}
              value={selectedKeys[0]}
              onChange={(e: any) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={confirm}
              onBlur={confirm}
            />
          </RBHBox>
        );
      },
      filterIcon: (filtered: any) => (
        <SearchOutlined data-testid={'search-menu-icon'} id={'search-menu-icon'} translate={null}
          style={{ color: filtered ? Colors.lightPurple : Colors.font.primary.gray }} />
      ),
      onFilter: (value: any, record: any) => {
        return record.menuName.toString().toLowerCase().includes(value.toLowerCase()) ||
          record.menuNameEN?.toString().toLowerCase().includes(value.toLowerCase());
      },
      onFilterDropdownVisibleChange: (visible: boolean) => {
        if (visible) {
          setTimeout(() => {
            const inputEl = document.getElementById('search-input');
            if (inputEl) {
              (inputEl as HTMLInputElement).select();
            }
          });
        }
      },
      sorter: (a: any, b: any) => a.menuName.localeCompare(b.menuName),
      render: (value: string, all: FoodMenuType, index: number) => {
        const indexId = `id-name-${index}`;
        return <RBHBox className={'box-menu-name'} style={{ width: metrics.tableHeader.width350 }} id={indexId}>
          <RBHBox className={'menu-image-content'} style={styles.imageBox}>
            <RBHImage id={'image_upload'} name={'image_upload'} style={styles.image} src={all.menuPhoto || Images.menuTableDefault}
              onError={(e: any) => e.target.src = Images.menuTableDefault} />
          </RBHBox>
          <RBHBox style={styles.menuNameBox}>
            <RBHBox className={'menu-name-text'} style={styles.menuNameBox.menuNameText}>
              {value}
            </RBHBox>
            <RBHBox className={'menu-name-en-text'} style={styles.menuNameBox.menuNameENText}>
              {all.menuNameEN}
            </RBHBox>
            <MenuDiscountLabel shouldDisplay={enableStrikethrough} menus={all.menuDiscounts} />
          </RBHBox>
        </RBHBox>;
      }
    },
    {
      width: metrics.tableHeader.width130,
      title: <FormattedMessage id={'main.foodmenu.landing.table.price'} />,
      className: 'menu-price-header',
      dataIndex: 'menuOriginalPrice',
      align: 'left',
      sorter: (a: any, b: any) => a.menuPrice - b.menuPrice,
      render: (value: any, all: any, index: number) => {
        const indexId = `id-price-${index}`;
        return (
          <RBHBox style={{
            width: metrics.tableHeader.width80,
            ...textStyles.subtitle2,
            fontSize: Fonts.size.text12
          }} id={indexId}>
            {UtilHelper.currencyFormat(value)}
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width130,
      className: 'menu-category-header',
      title: <FormattedMessage id={'main.foodmenu.landing.table.category'} />,
      sorter: (a: any, b: any) => a.menuCategory.menuCategoryName.localeCompare(b.menuCategory.menuCategoryName),
      render: (value: any, all: any, index: number) => {
        const menuCategoryName = 'menuCategoryName-' + index;
        const menuCategoryNameThStyle = all.menuCategory.menuCategoryTypeId === 2 ? styleText.textMediumFakeBlack : styleText.textTableContent
        const menuCategoryNameEnStyle = all.menuCategory.menuCategoryTypeId === 2 ? styleText.textMediumFakeBlack : styleText.textRegularGray
        return (
          <RBHBox id={menuCategoryName} style={{ width: metrics.tableHeader.width75 }}>
            <Row>
              {
                all.menuCategoryTypeId === 2 ?
                <Col>
                  <img src={Images.promotion} style={styleImage.iconS} />
                </Col> : null
              }
              <Col>
                <RBHText
                  id={`menuCategoryNameTH-${index}`} data-testid={`menuCategoryNameTH-${index}`}
                  style={menuCategoryNameThStyle}
                >
                  {all.menuCategory.menuCategoryName}
                </RBHText>
                <RBHText
                  id={`menuCategoryNameEN-${index}`} data-testid={`menuCategoryNameEN-${index}`}
                  style={menuCategoryNameEnStyle}
                >
                  {all.menuCategory.menuCategoryNameEN}
                </RBHText>
              </Col>
            </Row>
          </RBHBox>
        );
      }
    },
    {
      width: metrics.tableHeader.width130,
      title: <FormattedMessage id={'main.foodmenu.landing.table.status'} />,
      dataIndex: 'menuStatus',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        return (
          <Menu style={styles.menu} selectable={false}>
            {
              menuStatusCode.map((code: string) => {
                return (
                  <Menu.Item
                    key={code}
                    id={code}
                    style={selectedKeys[0] === code ? styles.selectedMenuItem : {}}
                    onClick={() => onMenuItemClick(setSelectedKeys, selectedKeys, confirm, clearFilters, code)}
                  >
                    {MainHelper.getMasterDataByCode(code)}
                  </Menu.Item>
                );
              })
            }
          </Menu>
        );
      },
      filterIcon: (filtered: any) => (
        <FilterFilled id={'filter-status-icon'} translate={null} style={{ color: filtered ? Colors.lightPurple : Colors.font.primary.gray }} />
      ),
      onFilter: (value: boolean, record: any) => {
        return record.menuStatus === value;
      },
      render: (value: string, all: any, index: number) => {
        const indexId = `id-status-${index}`;
        const qcIndexId = 'id-menu-qc-status-' + index;
        return (<>
          <RBHBox style={{ width: metrics.tableHeader.width100, ...textStyles.subtitle2, fontSize: Fonts.size.text12 }}
            id={indexId}>{MainHelper.getMasterDataByCode(value)}</RBHBox>
          <RBHQCTag qcStatus={all?.menuQCStatus} id={qcIndexId} marginTop={2} />
        </>);
      }
    },
    {
      width: metrics.tableHeader.width30,
      title: <FormattedMessage id={'main.foodmenu.landing.table.edit'} />,
      align: 'center',
      render: (value: any, all: any, index: number) => {
        const indexId = `id-edit-${index}`;
        const indexIdIcon = `id-edit-icon-${index}`;
        return (
          <RBHBox id={indexId} onClick={() => {
            const onSuccessCallback = (tagMaster: Tags[]) => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              const tag = tagMaster.find(value => value.tagId === all.tagId)
              if (tag?.isDeleted) {
                setEdit(all);
              } else {
                setEdit({ ...all, tag });
              };
            }
            dispatch(catCreators.callFoodCategory(noop, false, onSuccessCallback))
          }}>
            <EditOutlined data-testid={indexIdIcon} id={indexIdIcon} translate={null} style={styleImage.iconTable} />
          </RBHBox>);
      },
    },
    {
      width: metrics.tableHeader.width30,
      title: <FormattedMessage id={'main.foodmenu.landing.table.delete'} />,
      align: 'center',
      dataIndex: 'menuId',
      render: (value: any, all: any, index: number) => {
        const indexId = `id-delete-${index}`;
        const indexIdIcon = `id-delete-Icon-${index}`;
        return (
          <RBHBox id={indexId}>
            <DeleteOutlined data-testid={indexIdIcon} id={indexIdIcon}
              onClick={() => dispatchModal(value)} translate={null} style={styleImage.iconTable} />
          </RBHBox>);
      },
    }
  ];

  const getBreadcrumb = () => {
    return (
      <Breadcrumb style={styleText.breadCrumb}>
        <Breadcrumb.Item>
          <a data-testid={'breadcrumb_homepage_index'} id={'breakcrumb-homepage'} style={styleText.breadCrumbItemLink} onClick={() => {
            history.push('/main');
          }}>
            <FormattedMessage id={'main.main'} />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span data-testid={'breadcrumb_menu_landing_index'} id={'breakcrumb-menu-landing'} style={styleText.breadCrumbItem}>
            <FormattedMessage id={'main.foodmenu.landing.breadcrumb'} />
          </span>
        </Breadcrumb.Item>
      </Breadcrumb>
    );
  };

  const getEditBreadcrumbRender = () => {
    return (
      <RBHBox>
        <Breadcrumb style={styleText.breadCrumb}>
          <Breadcrumb.Item>
            <a data-testid={'breadcrumb_homepage_edit'} id={'breadcrumb-menu-edit-tohomepage'} style={styleText.breadCrumbItemLink} onClick={() =>
              dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
                visible: true, onOk: pushToMain, type: MODAL_TYPE.CONFIRM_EDIT
              }))}>
              <FormattedMessage id={'main.main'} />
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <a data-testid={'breadcrumb_menu_list_edit'} id={'breadcrumb-menu-edit-tolistcat'} style={styleText.breadCrumbItemLink} onClick={() =>
              dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
                visible: true, onOk: pushToMenu, type: MODAL_TYPE.CONFIRM_EDIT
              }))}>
              <FormattedMessage id={'main.foodmenu.landing.breadcrumb'} />
            </a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span data-testid={'breadcrumb_menu_edit_edit'} id={'breadcrumb-menu-edit'} style={styleText.breadCrumbItem}>
              <FormattedMessage id={'main.edit_foodmenu'} />
            </span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </RBHBox>
    );
  };

  const getTable = () => {
    return (
      <RBHBox style={styles.tableWrapper}>
        <RBHTable
          rowKey={'menuId'}
          id={'food-menu-table'}
          columns={columns}
          dataSource={foodMenuData}
          style={{ overflow: 'auto' }}
          rowClassName={(record: any) => {
            if (record.menuStatus === menuStatusCode[1]) {
              return 'disabled';
            }
            return '';
          }}
          showSorterTooltip={false}
          pagination={{
            showSizeChanger: false,
            pageSize: DEFAULT_PAGE_SIZE,
            position: ['bottomLeft'],
            hideOnSinglePage: true,
            showTotal: (total: number, range: number[]) => {
              return (
                <RBHText style={styleText.textPaginationShowTotal}>
                  <FormattedMessage id={'main.showing'} />
                  <span> {`${range[0]}-${range[1]}`} </span>
                  <FormattedMessage id={'main.of'} />
                  <span> {total}</span>
                </RBHText>
              );
            }
          }}
        >
        </RBHTable>
      </RBHBox>
    );
  };

  const addMenu = () => {
    history.push('/main/addmenu');
  };

  const AddMenuButton = () => {
    return (
      <RBHButton
        id={'add-menu-button'}
        data-testid={'add-menu-button'}
        style={styleButton.buttonAdd}
        text={'main.foodmenu.addmenu.breadcrumb'}
        type={'primary'}
        onClick={addMenu}>
      </RBHButton>
    );
  };

  const getEmpty = () => {
    return (
      <RBHBox style={styles.emptyWrapper}>
        <RBHEmpty textTitle={'main.foodmenu.empty.header'} textContent={'main.foodmenu.empty.content'}>
          <AddMenuButton />
        </RBHEmpty>
      </RBHBox>
    );
  };

  const qcRejectReasons = () => {
    // @ts-ignore
    return edit?.menuRejectReason?.map((reasons: string, index: number) => {
      return (
        <RBHText style={styles.qcRejectReasons} data-testid={`qcRejectReasons-${index}`}
          id={`qcRejectReasons-${index}`} key={index}>
          {' ' + '• ' + reasons}
        </RBHText>
      );
    });
  };

  const messageQc = () => {
    return (
      <RBHBox>
        <RBHText style={styles.messageQc} data-testid={'qcRejectReasonHeader'} id={'qcRejectReasonHeader'}
          // @ts-ignore
          text={edit?.menuRejectReason?.length === 0 ?
            'main.foodmenu.addEdit.qc.reject.header1' : 'main.foodmenu.addEdit.qc.reject.reasons.header1'} />
        {
          // @ts-ignore
          edit?.menuQCStatus !== 'MNQC-REJECT' || edit?.menuRejectReason?.length === 0 ? null :
            <RBHBox style={styles.qcRejectReasonsBox}>
              {qcRejectReasons()}
            </RBHBox>
        }
        <RBHText style={styles.messageQc} data-testid={'qcRejectReasonBottom'}
          id={'qcRejectReasonBottom'} text={'main.foodmenu.addEdit.qc.reject.header2'} />
      </RBHBox>
    );
  };

  const getContent = () => {
    if (loading) {
      return null;
    }

    const isError = foodMenuData === undefined;
    const isEmpty = !!(foodMenuData && foodMenuData.length === 0);
    return (
      <RBHBox style={styles.defaultContent}>
        <RBHBox style={styles.header}>
          <RBHText data-testid={'foodMenu_header'} id={'foodMenu_header'} text={'main.foodmenu.landing.header'} style={styleText.headerText} />
          {!(isEmpty || isError) && <AddMenuButton />}
        </RBHBox>
        <RBHBox style={styles.subTitleText} data-testid={'id-foodMenu-subtitle'} id={'id-foodMenu-subtitle'}>
          <FormattedMessage id={'main.foodmenu.subtitle'} />
        </RBHBox>
        {isError ? <RBHError onOk={() => resetReload()} /> : isEmpty ? getEmpty() : getTable()}
      </RBHBox>
    );
  };

  const getEditContent = () => {
    return (
      <RBHBox>
        <RBHBox style={styles.header}>
          <RBHText data-testid={'edit_foodMenu_header'} id={'id-text-edit-menu'} style={styleText.headerText} text={'main.edit_foodmenu'} />
        </RBHBox>
        <RBHBox style={styles.qaDescBox}>
          <RBHText data-testid={'addEditQcDesc'} id={'add_menu_header'} style={styles.qaDesc}
            text={'main.foodmenu.addEdit.qc.desc'} />
        </RBHBox>
        {
          // @ts-ignore
          edit?.menuQCStatus !== MNQC_STATUS.MNQC_REJECT ? null :
            <RBHBox style={styles.bannerBox}>
              <RBHBanner className={'bannerPadding'} style={styles.banner} message={messageQc()} />
            </RBHBox>
        }
        <RBHBox style={styles.editWrapper}>
          <FormAddEditMenu
            editData={edit}
            shouldEnableStrikethrough={enableStrikethrough}
            onSuccessCallback={pushToMenu}
            onCancel={() => dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, { visible: true, onOk: pushToMenu, type: MODAL_TYPE.CONFIRM_EDIT }))}
          />
        </RBHBox>
      </RBHBox>
    );
  };

  return (
    <RBHBox>
      {edit ? getEditBreadcrumbRender() : getBreadcrumb()}
      {edit ? getEditContent() : getContent()}
    </RBHBox>
  );
};

type Props = {};
